/**
 * Common error handling utilities for API calls
 */

export interface ErrorState {
  error: string | null;
  isError: boolean;
}

export interface SetErrorState {
  setError: (error: string | null) => void;
  setIsError: (isError: boolean) => void;
}

export type TranslationFunction = (key: string) => string;

/**
 * Clear error messages
 */
export const clearMessages = (
  setError: (error: string | null) => void,
  setIsError: (isError: boolean) => void
) => {
  setError(null);
  setIsError(false);
};

/**
 * Handle API response errors with consistent error message extraction
 */
export const handleApiError = async (
  response: Response,
  defaultMessage: string,
  t?: TranslationFunction
): Promise<string> => {
  if (response.status === 404) {
    return t ? t('errors.notFound') : "Resource not found. Please check the URL and try again.";
  } else if (response.status === 401 || response.status === 403) {
    return t ? t('errors.unauthorized') : "You don't have permission to access this resource. Please log in again.";
  } else if (response.status >= 500) {
    return t ? t('errors.serverError') : "Server error. Please try again later or contact support.";
  } else {
    // Try to get error details from response
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error ||
      errorData.message ||
      response.statusText ||
      defaultMessage;
    return errorMessage;
  }
};

/**
 * Handle network errors with consistent messaging
 */
export const handleNetworkError = (error: unknown, t?: TranslationFunction): string => {
  if (error instanceof Error) {
    // Check if it's a network error (fetch failed)
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return t ? t('errors.networkError') : "Network error: Unable to connect to the server. Please check your internet connection and try again.";
    } else {
      return error.message;
    }
  } else {
    return t ? t('errors.unexpectedError') : "An unexpected error occurred. Please try again.";
  }
};

/**
 * Standard error handling for API calls
 */
export const handleApiCall = async <T>(
  apiCall: () => Promise<Response>,
  defaultErrorMessage: string,
  setError: (error: string | null) => void,
  setIsError: (isError: boolean) => void,
  t?: TranslationFunction
): Promise<T | null> => {
  try {
    const response = await apiCall();

    if (!response.ok) {
      const errorMessage = await handleApiError(response, defaultErrorMessage, t);
      setError(errorMessage);
      setIsError(true);
      return null;
    }

    return await response.json();
  } catch (error) {
    const errorMessage = handleNetworkError(error, t);
    setError(errorMessage);
    setIsError(true);
    return null;
  }
};
